import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Stage, Layer, Rect, Text, Image as KImage, Line, Circle, Transformer } from "react-konva";
import Konva from "konva";
import type { DocV1, NodeAny, NodeBarcode, NodeCircle, NodeIcon, NodeImage, NodeLine, NodeQrcode, NodeRect, NodeText } from "./types";
import { uid } from "./id";
import { FONT_FAMILIES } from "./fonts";
import { exportDocToSvg } from "./exportSvg";
import { archiveTemplate, getOrCreateUid, getTemplate, listTemplatesArchived, saveTemplate, unarchiveTemplate, type StoredTemplate } from "./api";
import { iconToSvg, type IconKey } from "./icons";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { ean13DigitsOnly, makeBarcodeDataUrl } from "./barcodeQr";
import {
  IcoAlignB,
  IcoAlignC,
  IcoAlignL,
  IcoAlignM,
  IcoAlignR,
  IcoAlignT,
  IcoBarcode,
  IcoCircle,
  IcoClass,
  IcoDistX,
  IcoDistY,
  IcoImage,
  IcoLayerBack,
  IcoLayerDown,
  IcoLayerFwd,
  IcoLayerUp,
  IcoLine,
  IcoQr,
  IcoRedo,
  IcoRect,
  IcoSpreadX,
  IcoSpreadY,
  IcoText,
  IcoUndo,
} from "./editorIcons";

const MIN_STAGE_W = 760;
const MIN_STAGE_H = 520;
const LABEL_FRAME_PAD = 32;

type Bounds = { x: number; y: number; w: number; h: number };

function cn(...parts: (string | false | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

/** title + aria-label (эквивалент alt для кнопок с иконкой) */
function icoA11y(label: string) {
  return { title: label, "aria-label": label };
}

function useIsMobile(breakpoint = 900) {
  const [m, setM] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(`(max-width: ${breakpoint}px)`).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const u = () => setM(mq.matches);
    u();
    mq.addEventListener("change", u);
    return () => mq.removeEventListener("change", u);
  }, [breakpoint]);
  return m;
}

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

function downloadText(filename: string, text: string, mime = "text/plain") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function waitFrames(n: number) {
  return new Promise<void>((resolve) => {
    let i = 0;
    const step = () => {
      i += 1;
      if (i >= n) resolve();
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

function useKonvaImage(src?: string) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!src) return;
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = src;
  }, [src]);
  return img;
}

function mmToPxAtEditor(mm: number) {
  // Editor is arbitrary pixels; keep label size comfortable on screen.
  return mm * 8;
}

function defaultDoc(): DocV1 {
  return {
    version: 1,
    name: "Наклейка",
    sizeMM: { w: 80, h: 50 },
    bw: true,
    nodes: [],
  };
}

function snapToGuides(
  x: number,
  y: number,
  columns: number,
  gutterPx: number,
  stageW: number,
  stageH: number,
) {
  const usableW = stageW - gutterPx * 2;
  const colW = usableW / columns;
  const guidesX: number[] = [gutterPx, gutterPx + usableW, gutterPx + usableW / 2];
  for (let i = 0; i <= columns; i++) guidesX.push(gutterPx + i * colW);
  const guidesY: number[] = [gutterPx, stageH - gutterPx, stageH / 2];

  const snap = 6;
  const sx = guidesX.reduce((best, g) => (Math.abs(g - x) < Math.abs(best - x) ? g : best), guidesX[0]);
  const sy = guidesY.reduce((best, g) => (Math.abs(g - y) < Math.abs(best - y) ? g : best), guidesY[0]);
  return {
    x: Math.abs(sx - x) <= snap ? sx : x,
    y: Math.abs(sy - y) <= snap ? sy : y,
    guidesX,
    guidesY,
  };
}

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const key = "labler_cookie_ok_v1";
    if (localStorage.getItem(key) !== "1") setShow(true);
  }, []);
  if (!show) return null;
  return (
    <div className="cookie" role="dialog" aria-label="Уведомление о cookies">
      <div className="cookieText">
        <b>Мы используем cookies</b>, чтобы сервис работал стабильнее (например, для сохранения настроек). Продолжая,
        вы соглашаетесь с этим.
      </div>
      <button
        className="btn primary"
        onClick={() => {
          localStorage.setItem("labler_cookie_ok_v1", "1");
          setShow(false);
        }}
      >
        Понятно
      </button>
    </div>
  );
}

function bwColor(doc: DocV1) {
  return doc.bw ? "#111111" : "#111111";
}

function getNodeBounds(n: NodeAny): Bounds {
  switch (n.type) {
    case "text": {
      const lines = n.text.split("\n").length;
      const h = Math.max(1, lines) * n.fontSize * 1.2;
      return { x: n.x, y: n.y, w: n.width, h };
    }
    case "image":
      return { x: n.x, y: n.y, w: n.w, h: n.h };
    case "barcode":
      return { x: n.x, y: n.y, w: n.w, h: n.h };
    case "qrcode":
      return { x: n.x, y: n.y, w: n.w, h: n.h };
    case "icon":
      return { x: n.x, y: n.y, w: n.w, h: n.h };
    case "rect":
      return { x: n.x, y: n.y, w: n.w, h: n.h };
    case "circle":
      return { x: n.x, y: n.y, w: n.r * 2, h: n.r * 2 };
    case "line": {
      const xs: number[] = [];
      const ys: number[] = [];
      for (let i = 0; i < n.points.length; i += 2) {
        xs.push(n.points[i]);
        ys.push(n.points[i + 1]);
      }
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      return { x: n.x + minX, y: n.y + minY, w: maxX - minX, h: maxY - minY };
    }
    default:
      // If we ever add a new node type and forget to handle it,
      // this will fail at compile time.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _exhaustive: never = n;
      return { x: 0, y: 0, w: 10, h: 10 };
  }
}

function unionBounds(bounds: Bounds[]): Bounds {
  const minX = Math.min(...bounds.map((b) => b.x));
  const minY = Math.min(...bounds.map((b) => b.y));
  const maxX = Math.max(...bounds.map((b) => b.x + b.w));
  const maxY = Math.max(...bounds.map((b) => b.y + b.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function App() {
  return (
    <ErrorBoundary>
      <InnerApp />
    </ErrorBoundary>
  );
}

function InnerApp() {
  const [tabs, setTabs] = useState<{ id: string; doc: DocV1 }[]>(() => [{ id: uid("tab"), doc: defaultDoc() }]);
  const [activeTabId, setActiveTabId] = useState(() => tabs[0]?.id ?? "tab0");
  const activeTab = useMemo(() => tabs.find((t) => t.id === activeTabId) ?? tabs[0], [tabs, activeTabId]);
  const doc = activeTab?.doc ?? defaultDoc();
  const setDoc = (next: DocV1 | ((prev: DocV1) => DocV1)) => {
    setTabs((prevTabs) =>
      prevTabs.map((t) => {
        if (t.id !== activeTabId) return t;
        const resolved = typeof next === "function" ? (next as any)(t.doc) : next;
        return { ...t, doc: resolved };
      }),
    );
  };

  const [selectedByTab, setSelectedByTab] = useState<Record<string, string[]>>({});
  const selectedIds = selectedByTab[activeTabId] ?? [];
  const setSelectedIds = (ids: string[] | ((prev: string[]) => string[])) => {
    setSelectedByTab((m) => {
      const prev = m[activeTabId] ?? [];
      const next = typeof ids === "function" ? (ids as any)(prev) : ids;
      return { ...m, [activeTabId]: next };
    });
  };
  const [templates, setTemplates] = useState<StoredTemplate[]>([]);
  const [templatesArchived, setTemplatesArchived] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [columns, setColumns] = useState(6);
  const [gutter, setGutter] = useState(24);
  const transformerRef = useRef<any>(null);
  const nodeRefs = useRef(new Map<string, any>());
  const stageRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewportW, setViewportW] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  const [zoom, setZoom] = useState(1);
  const [templateQuery, setTemplateQuery] = useState("");
  const [dupCount, setDupCount] = useState(5);
  const [dupStepX, setDupStepX] = useState(16);
  const [dupStepY, setDupStepY] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectStart, setSelectStart] = useState<{ x: number; y: number } | null>(null);
  const [selectRect, setSelectRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [exporting, setExporting] = useState(false);
  const isMobile = useIsMobile(900);
  const [mobileTab, setMobileTab] = useState<"tools" | "canvas" | "props">("canvas");
  const clipboardRef = useRef<NodeAny[] | null>(null);

  const { stageW, stageH, labelW, labelH, labelX, labelY } = useMemo(() => {
    const lw = mmToPxAtEditor(doc.sizeMM.w);
    const lh = mmToPxAtEditor(doc.sizeMM.h);
    const sw = Math.max(MIN_STAGE_W, Math.ceil(lw + LABEL_FRAME_PAD * 2));
    const sh = Math.max(MIN_STAGE_H, Math.ceil(lh + LABEL_FRAME_PAD * 2));
    return {
      stageW: sw,
      stageH: sh,
      labelW: lw,
      labelH: lh,
      labelX: (sw - lw) / 2,
      labelY: (sh - lh) / 2,
    };
  }, [doc.sizeMM.w, doc.sizeMM.h]);

  const labelRect = useMemo(
    () => ({ x: labelX, y: labelY, w: labelW, h: labelH }),
    [labelX, labelY, labelW, labelH],
  );

  const fitScale = Math.min(1, viewportW / stageW);
  const stageScale = exporting ? 1 : fitScale * zoom;

  const [sizeWText, setSizeWText] = useState(() => String(doc.sizeMM.w));
  const [sizeHText, setSizeHText] = useState(() => String(doc.sizeMM.h));
  useEffect(() => {
    setSizeWText(String(doc.sizeMM.w));
    setSizeHText(String(doc.sizeMM.h));
  }, [doc.sizeMM.w, doc.sizeMM.h, activeTabId]);

  const selectedNodes = useMemo(() => doc.nodes.filter((n) => selectedIds.includes(n.id)), [doc.nodes, selectedIds]);
  const selected = selectedNodes.length === 1 ? selectedNodes[0] : null;
  const transformerKeepRatio = useMemo(() => {
    if (selectedNodes.length !== 1) return false;
    const n = selectedNodes[0];
    if (n.type === "circle") return true;
    return false;
  }, [selectedNodes]);
  const prevSelectedRef = useRef<string>("");
  useEffect(() => {
    if (!isMobile) return;
    const key = selectedIds.join(",");
    if (key !== prevSelectedRef.current && selectedIds.length === 1) {
      setMobileTab("props");
    }
    prevSelectedRef.current = key;
  }, [isMobile, selectedIds]);

  // Undo stack per tab
  const undoByTabRef = useRef<Record<string, { past: DocV1[]; future: DocV1[] }>>({});

  function setDocUndo(next: DocV1 | ((prev: DocV1) => DocV1)) {
    setTabs((prevTabs) => {
      const stacks = undoByTabRef.current[activeTabId] ?? { past: [], future: [] };
      undoByTabRef.current[activeTabId] = stacks;
      return prevTabs.map((t) => {
        if (t.id !== activeTabId) return t;
        const resolved = typeof next === "function" ? (next as any)(t.doc) : next;
        stacks.past.push(t.doc);
        if (stacks.past.length > 80) stacks.past.shift();
        stacks.future = [];
        return { ...t, doc: resolved };
      });
    });
  }

  function undo() {
    const past = (undoByTabRef.current[activeTabId] ?? { past: [], future: [] }).past;
    if (past.length === 0) return;
    setTabs((prevTabs) => {
      const stacks = undoByTabRef.current[activeTabId] ?? { past: [], future: [] };
      undoByTabRef.current[activeTabId] = stacks;
      const prevDoc = stacks.past.pop()!;
      return prevTabs.map((t) => {
        if (t.id !== activeTabId) return t;
        stacks.future.push(t.doc);
        return { ...t, doc: prevDoc };
      });
    });
  }

  function redo() {
    const future = (undoByTabRef.current[activeTabId] ?? { past: [], future: [] }).future;
    if (future.length === 0) return;
    setTabs((prevTabs) => {
      const stacks = undoByTabRef.current[activeTabId] ?? { past: [], future: [] };
      undoByTabRef.current[activeTabId] = stacks;
      const nextDoc = stacks.future.pop()!;
      return prevTabs.map((t) => {
        if (t.id !== activeTabId) return t;
        stacks.past.push(t.doc);
        return { ...t, doc: nextDoc };
      });
    });
  }

  function isTypingTarget(t: any) {
    const tag = String(t?.tagName || "").toUpperCase();
    return tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable;
  }

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      setViewportW(Math.max(320, w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageW]);

  useEffect(() => {
    (async () => {
      try {
        const items = await listTemplatesArchived(templatesArchived);
        setTemplates(items);
      } catch {
        // ignore
      }
    })();
  }, [templatesArchived]);

  useEffect(() => {
    // Load template by URL hash: /app#t=<id>
    const m = new URLSearchParams(location.hash.replace(/^#/, ""));
    const id = m.get("t");
    if (!id) {
      // Restore local draft for this anonymous user (if present).
      try {
        const u = getOrCreateUid();
        const raw = localStorage.getItem(`labler_draft_v2:${u}`);
        if (raw) {
          const parsed = JSON.parse(raw) as { activeTabId?: string; tabs?: { id: string; doc: DocV1 }[] };
          if (Array.isArray(parsed.tabs) && parsed.tabs.length > 0) {
            setTabs(parsed.tabs);
            if (parsed.activeTabId && parsed.tabs.some((t) => t.id === parsed.activeTabId)) {
              setActiveTabId(parsed.activeTabId);
            }
          }
        }
      } catch {
        // ignore
      }
      return;
    }
    (async () => {
      try {
        setBusy("Загрузка шаблона…");
        const d = await getTemplate(id);
        setDoc(d);
        setSelectedIds([]);
      } finally {
        setBusy(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (exporting) return;
    const t = window.setTimeout(() => {
      try {
        const u = getOrCreateUid();
        localStorage.setItem(`labler_draft_v2:${u}`, JSON.stringify({ activeTabId, tabs }));
      } catch {
        // ignore
      }
    }, 450);
    return () => window.clearTimeout(t);
  }, [tabs, activeTabId, exporting]);

  function addTab(withDoc?: DocV1) {
    const id = uid("tab");
    const nextDoc = withDoc ?? defaultDoc();
    setTabs((prev) => {
      if (prev.length >= 8) return prev;
      return [...prev, { id, doc: nextDoc }];
    });
    setActiveTabId(id);
    setSelectedIds([]);
  }

  function closeTab(id: string) {
    setTabs((prev) => {
      if (prev.length <= 1) return prev;
      const idx = prev.findIndex((t) => t.id === id);
      const next = prev.filter((t) => t.id !== id);
      if (activeTabId === id) {
        const fallback = next[Math.max(0, idx - 1)] ?? next[0];
        setActiveTabId(fallback.id);
        setSelectedByTab((m) => ({ ...m, [fallback.id]: m[fallback.id] ?? [] }));
      }
      return next;
    });
  }

  function switchTab(id: string) {
    setActiveTabId(id);
    setSelectedIds([]);
  }

  useLayoutEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    if (exporting) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const nodes = selectedIds.map((id) => nodeRefs.current.get(id)).filter(Boolean);
    tr.nodes(nodes);
    tr.getLayer()?.batchDraw();
  }, [selectedIds, doc.nodes, exporting]);

  function updateNode(id: string, patch: Partial<NodeAny>) {
    setDocUndo((d) => ({
      ...d,
      nodes: d.nodes.map((n) => (n.id === id ? ({ ...n, ...patch } as NodeAny) : n)),
    }));
  }

  function removeSelected() {
    if (selectedIds.length === 0) return;
    const del = new Set(selectedIds);
    setDocUndo((d) => ({ ...d, nodes: d.nodes.filter((n) => !del.has(n.id)) }));
    setSelectedIds([]);
  }

  function copySelected() {
    if (selectedIds.length === 0) return;
    const byId = new Set(selectedIds);
    const nodes = doc.nodes.filter((n) => byId.has(n.id));
    clipboardRef.current = JSON.parse(JSON.stringify(nodes));
    setBusy("Скопировано");
    window.setTimeout(() => setBusy(null), 700);
  }

  function pasteClipboard() {
    const src = clipboardRef.current;
    if (!src || src.length === 0) return;
    const dx = 12;
    const dy = 12;
    const pasted = src.map((n) => {
      const nextId = uid(n.type.slice(0, 2));
      return { ...(n as any), id: nextId, x: (n as any).x + dx, y: (n as any).y + dy } as NodeAny;
    });
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, ...pasted] }));
    setSelectedIds(pasted.map((n) => n.id));
  }

  async function onAddImage(file: File) {
    const dataUrl = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(String(fr.result));
      fr.onerror = () => rej(new Error("Не удалось прочитать файл"));
      fr.readAsDataURL(file);
    });
    // Keep original aspect ratio on insert to avoid initial distortion.
    const dims = await new Promise<{ w: number; h: number }>((res) => {
      const img = new Image();
      img.onload = () => res({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
      img.onerror = () => res({ w: 1, h: 1 });
      img.src = dataUrl;
    });
    const maxW = 320;
    const maxH = 220;
    const s = Math.min(maxW / dims.w, maxH / dims.h, 1);
    const w = Math.max(10, Math.round(dims.w * s));
    const h = Math.max(10, Math.round(dims.h * s));
    const n: NodeImage = {
      id: uid("img"),
      type: "image",
      x: 120,
      y: 120,
      rotation: 0,
      opacity: 1,
      srcDataUrl: dataUrl,
      w,
      h,
      lockAspect: false,
      naturalW: w,
      naturalH: h,
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  function addText() {
    const n: NodeText = {
      id: uid("txt"),
      type: "text",
      x: 120,
      y: 80,
      rotation: 0,
      opacity: 1,
      text: "Новый текст",
      fontFamily: "Inter",
      fontSize: 28,
      fontStyle: "normal",
      fontWeight: 300,
      fill: bwColor(doc),
      align: "left",
      width: 320,
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  function addLine() {
    const n: NodeLine = {
      id: uid("ln"),
      type: "line",
      x: 150,
      y: 220,
      rotation: 0,
      opacity: 1,
      points: [0, 0, 240, 0],
      stroke: doc.bw ? "#111111" : "#111111",
      strokeWidth: 1,
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  function addRect() {
    const n: NodeRect = {
      id: uid("rc"),
      type: "rect",
      x: 140,
      y: 140,
      rotation: 0,
      opacity: 1,
      w: 240,
      h: 120,
      fill: "transparent",
      stroke: doc.bw ? "#111111" : "#111111",
      strokeWidth: 1,
      cornerRadius: 12,
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  function addCircle() {
    const n: NodeCircle = {
      id: uid("cc"),
      type: "circle",
      x: 160,
      y: 160,
      rotation: 0,
      opacity: 1,
      r: 60,
      fill: "transparent",
      stroke: doc.bw ? "#111111" : "#111111",
      strokeWidth: 1,
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  function addIcon(icon: IconKey) {
    const n: NodeIcon = {
      id: uid("ic"),
      type: "icon",
      x: 420,
      y: 120,
      rotation: 0,
      opacity: 1,
      icon,
      w: 100,
      h: 100,
      fill: doc.bw ? "#111111" : "#111111",
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  function addBarcode(format: NodeBarcode["format"]) {
    const n: NodeBarcode = {
      id: uid("bc"),
      type: "barcode",
      x: 180,
      y: 320,
      rotation: 0,
      opacity: 1,
      format,
      value: format === "EAN13" ? "5901234123457" : "LABLER-001",
      w: 360,
      h: 120,
      lineColor: "#111111",
      background: "transparent",
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  function addQrCode() {
    const n: NodeQrcode = {
      id: uid("qr"),
      type: "qrcode",
      x: 220,
      y: 200,
      rotation: 0,
      opacity: 1,
      value: "https://labler.ru",
      w: 140,
      h: 140,
      foreground: doc.bw ? "#111111" : "#111111",
      background: "transparent",
    };
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, n] }));
    setSelectedIds([n.id]);
  }

  async function save() {
    try {
      setBusy("Сохранение…");
      const res = await saveTemplate(doc);
      try {
        (window as any).ym?.(108770371, "reachGoal", "save_template");
      } catch {
        // ignore
      }
      const items = await listTemplatesArchived(templatesArchived);
      setTemplates(items);
      const sp = new URLSearchParams(location.hash.replace(/^#/, ""));
      sp.set("t", res.id);
      location.hash = sp.toString();
    } finally {
      setBusy(null);
    }
  }

  function exportJson() {
    downloadText(`${doc.name || "labler"}.labler.json`, JSON.stringify(doc, null, 2), "application/json");
  }

  function docForExport(d: DocV1): DocV1 {
    if (!d.bw) return d;
    const fix = (c: string) => (c === "transparent" ? c : "#111111");
    return {
      ...d,
      nodes: d.nodes.map((n) => {
        if (n.type === "text") return { ...n, fill: fix(n.fill) } as any;
        if (n.type === "line") return { ...n, stroke: fix(n.stroke) } as any;
        if (n.type === "rect") return { ...n, stroke: fix(n.stroke), fill: n.fill === "transparent" ? "transparent" : "#111111" } as any;
        if (n.type === "circle") return { ...n, stroke: fix(n.stroke), fill: n.fill === "transparent" ? "transparent" : "#111111" } as any;
        if (n.type === "barcode") return { ...n, lineColor: "#111111" } as any;
        if (n.type === "qrcode") return { ...n, foreground: "#111111" } as any;
        if (n.type === "icon") return { ...n, fill: "#111111" } as any;
        return n as any;
      }),
    };
  }

  async function exportSvg() {
    const prevTab = mobileTab;
    let stage: any = null;
    let sx = 1;
    let sy = 1;
    try {
      setExporting(true);
      if (isMobile) setMobileTab("canvas");
      await waitFrames(6);
      stage = stageRef.current;
      sx = stage?.scaleX() ?? 1;
      sy = stage?.scaleY() ?? 1;
      if (stage) {
        stage.scale({ x: 1, y: 1 });
        stage.batchDraw?.();
      }
      await waitFrames(2);
      const { svg } = await exportDocToSvg(docForExport(doc), labelRect);
      downloadText(`${doc.name || "labler"}.svg`, svg, "image/svg+xml");
      try {
        (window as any).ym?.(108770371, "reachGoal", "export_svg");
      } catch {
        // ignore
      }
    } finally {
      if (stage) {
        stage.scale({ x: sx, y: sy });
        stage.batchDraw?.();
      }
      if (isMobile) setMobileTab(prevTab);
      setExporting(false);
    }
  }

  async function exportPdf() {
    const prevTab = mobileTab;
    let stage: any = null;
    let sx = 1;
    let sy = 1;
    try {
      setExporting(true);
      if (isMobile) setMobileTab("canvas");
      await waitFrames(6);
      stage = stageRef.current;
      if (!stage) throw new Error("Stage недоступен");
      sx = stage.scaleX();
      sy = stage.scaleY();
      stage.scale({ x: 1, y: 1 });
      stage.batchDraw?.();
      await waitFrames(2);
      // Raster from Konva (same rendering as the editor) so Cyrillic matches the canvas;
      // vector PDF via svg2pdf did not embed fonts and broke Russian text.
      const pixelRatio = 5;
      const rx = Math.round(labelX);
      const ry = Math.round(labelY);
      const rw = Math.round(labelW);
      const rh = Math.round(labelH);
      const dataUrl = stage.toDataURL({
        x: rx,
        y: ry,
        width: rw,
        height: rh,
        pixelRatio,
        mimeType: "image/png",
      });
      const wMm = doc.sizeMM.w;
      const hMm = doc.sizeMM.h;
      // jsPDF swaps page sides when sizeMM.w > sizeMM.h but orientation is default "p" (portrait).
      const pdf = new jsPDF({
        unit: "mm",
        format: [wMm, hMm],
        orientation: wMm >= hMm ? "landscape" : "portrait",
        compress: true,
      });
      pdf.addImage(dataUrl, "PNG", 0, 0, wMm, hMm);
      pdf.save(`${doc.name || "labler"}.pdf`);
      try {
        (window as any).ym?.(108770371, "reachGoal", "export_pdf");
      } catch {
        // ignore
      }
    } finally {
      if (stage) {
        stage.scale({ x: sx, y: sy });
        stage.batchDraw?.();
      }
      if (isMobile) setMobileTab(prevTab);
      setExporting(false);
    }
  }

  const guideInfo = useMemo(() => snapToGuides(0, 0, columns, gutter, stageW, stageH), [columns, gutter, stageW, stageH]);
  const filteredTemplates = useMemo(() => {
    const q = templateQuery.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(q) || t.id.toLowerCase().includes(q));
  }, [templates, templateQuery]);

  async function refreshTemplates() {
    try {
      const items = await listTemplatesArchived(templatesArchived);
      setTemplates(items);
    } catch {
      // ignore
    }
  }

  function duplicateSelected() {
    if (selectedIds.length === 0) return;
    const sel = new Set(selectedIds);
    const copies = doc.nodes
      .filter((n) => sel.has(n.id))
      .map((n) => ({ ...n, id: uid("n"), x: n.x + 18, y: n.y + 18 } as NodeAny));
    if (copies.length === 0) return;
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, ...copies] }));
    setSelectedIds(copies.map((c) => c.id));
  }

  function duplicateSeries(dx: number, dy: number, count: number) {
    if (selectedIds.length === 0) return;
    const c = clamp(count, 1, 200);
    const sel = new Set(selectedIds);
    const originals = doc.nodes.filter((n) => sel.has(n.id));
    if (originals.length === 0) return;
    const copies: NodeAny[] = [];
    // "Копий" means number of copies (originals not included).
    for (let i = 1; i <= c; i++) {
      for (const n of originals) {
        copies.push({ ...n, id: uid("n"), x: n.x + dx * i, y: n.y + dy * i } as NodeAny);
      }
    }
    setDocUndo((d) => ({ ...d, nodes: [...d.nodes, ...copies] }));
    setSelectedIds(copies.map((x) => x.id));
  }

  function reorderSelected(direction: "front" | "back" | "forward" | "backward") {
    if (selectedIds.length === 0) return;
    const sel = new Set(selectedIds);
    setDocUndo((d) => {
      const nodes = [...d.nodes];
      if (direction === "front") {
        const picked = nodes.filter((n) => sel.has(n.id));
        const rest = nodes.filter((n) => !sel.has(n.id));
        return { ...d, nodes: [...rest, ...picked] };
      }
      if (direction === "back") {
        const picked = nodes.filter((n) => sel.has(n.id));
        const rest = nodes.filter((n) => !sel.has(n.id));
        return { ...d, nodes: [...picked, ...rest] };
      }
      // step move while preserving relative order of selected set
      if (direction === "forward") {
        for (let i = nodes.length - 2; i >= 0; i--) {
          if (!sel.has(nodes[i].id) && sel.has(nodes[i + 1].id)) {
            const tmp = nodes[i];
            nodes[i] = nodes[i + 1];
            nodes[i + 1] = tmp;
          }
        }
        return { ...d, nodes };
      }
      for (let i = 1; i < nodes.length; i++) {
        if (!sel.has(nodes[i].id) && sel.has(nodes[i - 1].id)) {
          const tmp = nodes[i];
          nodes[i] = nodes[i - 1];
          nodes[i - 1] = tmp;
        }
      }
      return { ...d, nodes };
    });
  }

  function alignToLabel(mode: "left" | "right" | "hcenter" | "top" | "bottom" | "vcenter") {
    if (selectedIds.length === 0) return;
    const labelBounds: Bounds = { x: labelX, y: labelY, w: labelW, h: labelH };
    setDocUndo((d) => ({
      ...d,
      nodes: d.nodes.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const b = getNodeBounds(n);
        if (mode === "left") return { ...n, x: n.x + (labelBounds.x - b.x) } as any;
        if (mode === "right") return { ...n, x: n.x + (labelBounds.x + labelBounds.w - (b.x + b.w)) } as any;
        if (mode === "hcenter") return { ...n, x: n.x + (labelBounds.x + labelBounds.w / 2 - (b.x + b.w / 2)) } as any;
        if (mode === "top") return { ...n, y: n.y + (labelBounds.y - b.y) } as any;
        if (mode === "bottom") return { ...n, y: n.y + (labelBounds.y + labelBounds.h - (b.y + b.h)) } as any;
        return { ...n, y: n.y + (labelBounds.y + labelBounds.h / 2 - (b.y + b.h / 2)) } as any;
      }),
    }));
  }

  function snapSelectionToColumns(mode: "x" | "left" | "center" | "right") {
    if (selectedIds.length === 0) return;
    const guides = guideInfo.guidesX;
    const snapTo = (x: number) =>
      guides.reduce((best, g) => (Math.abs(g - x) < Math.abs(best - x) ? g : best), guides[0]);
    setDocUndo((d) => ({
      ...d,
      nodes: d.nodes.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const b = getNodeBounds(n);
        const anchor =
          mode === "left" ? b.x : mode === "center" ? b.x + b.w / 2 : mode === "right" ? b.x + b.w : n.x;
        const snapped = snapTo(anchor);
        const dx = snapped - anchor;
        return { ...n, x: n.x + dx } as any;
      }),
    }));
  }

  function alignSelection(mode: "left" | "right" | "hcenter" | "top" | "bottom" | "vcenter") {
    if (selectedIds.length < 2) return;
    const nodes = doc.nodes.filter((n) => selectedIds.includes(n.id));
    const u = unionBounds(nodes.map(getNodeBounds));
    setDocUndo((d) => ({
      ...d,
      nodes: d.nodes.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const b = getNodeBounds(n);
        if (mode === "left") return { ...n, x: n.x + (u.x - b.x) } as any;
        if (mode === "right") return { ...n, x: n.x + (u.x + u.w - (b.x + b.w)) } as any;
        if (mode === "hcenter") return { ...n, x: n.x + (u.x + u.w / 2 - (b.x + b.w / 2)) } as any;
        if (mode === "top") return { ...n, y: n.y + (u.y - b.y) } as any;
        if (mode === "bottom") return { ...n, y: n.y + (u.y + u.h - (b.y + b.h)) } as any;
        return { ...n, y: n.y + (u.y + u.h / 2 - (b.y + b.h / 2)) } as any;
      }),
    }));
  }

  function distributeInLabel(axis: "x" | "y") {
    if (selectedIds.length < 2) return;
    const selected = doc.nodes.filter((n) => selectedIds.includes(n.id));
    const items = selected.map((n) => ({ n, b: getNodeBounds(n) }));
    if (axis === "x") {
      items.sort((a, b) => a.b.x - b.b.x);
      const sumW = items.reduce((s, it) => s + it.b.w, 0);
      const gap = (labelW - sumW) / (items.length + 1);
      if (!(gap >= 0) || !Number.isFinite(gap)) return;
      const updates = new Map<string, number>();
      let cursor = labelX + gap;
      for (const it of items) {
        updates.set(it.n.id, it.n.x + (cursor - it.b.x));
        cursor += it.b.w + gap;
      }
      setDocUndo((d) => ({
        ...d,
        nodes: d.nodes.map((n) => (updates.has(n.id) ? ({ ...n, x: updates.get(n.id)! } as any) : n)),
      }));
      return;
    }
    items.sort((a, b) => a.b.y - b.b.y);
    const sumH = items.reduce((s, it) => s + it.b.h, 0);
    const gap = (labelH - sumH) / (items.length + 1);
    if (!(gap >= 0) || !Number.isFinite(gap)) return;
    const updates = new Map<string, number>();
    let cursor = labelY + gap;
    for (const it of items) {
      updates.set(it.n.id, it.n.y + (cursor - it.b.y));
      cursor += it.b.h + gap;
    }
    setDocUndo((d) => ({
      ...d,
      nodes: d.nodes.map((n) => (updates.has(n.id) ? ({ ...n, y: updates.get(n.id)! } as any) : n)),
    }));
  }

  function distributeSelection(axis: "x" | "y") {
    if (selectedIds.length < 3) return;
    const nodes = doc.nodes.filter((n) => selectedIds.includes(n.id));
    const items = nodes
      .map((n) => ({ n, b: getNodeBounds(n) }))
      .sort((a, b) => (axis === "x" ? a.b.x - b.b.x : a.b.y - b.b.y));
    const first = items[0];
    const last = items[items.length - 1];
    const spanStart = axis === "x" ? first.b.x : first.b.y;
    const spanEnd = axis === "x" ? last.b.x + last.b.w : last.b.y + last.b.h;
    const totalSize = items.reduce((s, it) => s + (axis === "x" ? it.b.w : it.b.h), 0);
    const gap = (spanEnd - spanStart - totalSize) / (items.length - 1);
    if (!Number.isFinite(gap)) return;

    let cursor = spanStart;
    const updates = new Map<string, Partial<NodeAny>>();
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (i === 0 || i === items.length - 1) {
        cursor += (axis === "x" ? it.b.w : it.b.h) + gap;
        continue;
      }
      if (axis === "x") {
        updates.set(it.n.id, { x: it.n.x + (cursor - it.b.x) } as any);
        cursor += it.b.w + gap;
      } else {
        updates.set(it.n.id, { y: it.n.y + (cursor - it.b.y) } as any);
        cursor += it.b.h + gap;
      }
    }
    setDocUndo((d) => ({
      ...d,
      nodes: d.nodes.map((n) => (updates.has(n.id) ? ({ ...n, ...updates.get(n.id)! } as any) : n)),
    }));
  }

  function rectFromPoints(a: { x: number; y: number }, b: { x: number; y: number }) {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const w = Math.abs(a.x - b.x);
    const h = Math.abs(a.y - b.y);
    return { x, y, w, h };
  }

  function intersects(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }) {
    return a.x <= b.x + b.w && a.x + a.w >= b.x && a.y <= b.y + b.h && a.y + a.h >= b.y;
  }

  return (
    <>
      <div className="app">
        <div className="topbar">
        <a className="brand" href="/" {...icoA11y("Открыть главную страницу LABLER.RU")}>
          <div className="brandMark">L</div>
          <div className="appBrandTitle">LABLER.RU</div>
          {busy ? <div className="small">• {busy}</div> : null}
        </a>
        <div className="topActions">
          <button className={cn("btn", showTemplates && "primary")} onClick={() => setShowTemplates((v) => !v)}>
            Шаблоны
          </button>
          <button className="btn" onClick={save}>
            Сохранить шаблон
          </button>
          <button className="btn" onClick={exportJson}>
            Скачать шаблон
          </button>
          <button
            className="btn"
            onClick={() => {
              void exportSvg();
            }}
          >
            SVG
          </button>
          <button
            className="btn primary"
            onClick={() => {
              void exportPdf();
            }}
          >
            PDF
          </button>
        </div>
        </div>

        {showTemplates ? (
          <div className="topPopover">
            <div className="topPopover__header">
              <div className="h2" style={{ margin: 0 }}>
                Шаблоны
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button className={cn("btn", templatesArchived && "primary")} onClick={() => setTemplatesArchived((v) => !v)}>
                  {templatesArchived ? "Активные" : "Архив"}
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    void (async () => {
                      try {
                        setBusy("Обновление…");
                        const items = await listTemplatesArchived(templatesArchived);
                        setTemplates(items);
                      } finally {
                        setBusy(null);
                      }
                    })();
                  }}
                >
                  Обновить
                </button>
                <button className="btn" onClick={() => addTab()}>
                  + Вкладка
                </button>
                <button className="btn" onClick={() => setShowTemplates(false)}>
                  Закрыть
                </button>
              </div>
            </div>
            <div className="field" style={{ marginTop: 8 }}>
              <input className="input" placeholder="Поиск…" value={templateQuery} onChange={(e) => setTemplateQuery(e.target.value)} />
            </div>
            <div className="tplList">
              {filteredTemplates.map((t) => (
                <div key={t.id} className="tplRow">
                  <div style={{ minWidth: 0 }}>
                    <div className="tplName">{t.name || t.id}</div>
                    <div className="small">{new Date(t.updatedAt).toLocaleString()}</div>
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <button
                      className="btn primary"
                      onClick={() => {
                        void (async () => {
                          try {
                            setBusy("Загрузка…");
                            const d = await getTemplate(t.id);
                            setDoc(d);
                            setSelectedIds([]);
                            setShowTemplates(false);
                          } finally {
                            setBusy(null);
                          }
                        })();
                      }}
                    >
                      Открыть
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        void (async () => {
                          try {
                            setBusy("Загрузка…");
                            const d = await getTemplate(t.id);
                            addTab(d);
                          } finally {
                            setBusy(null);
                          }
                        })();
                      }}
                    >
                      В новую
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        void (async () => {
                          try {
                            setBusy(templatesArchived ? "Возврат…" : "Архив…");
                            if (templatesArchived) await unarchiveTemplate(t.id);
                            else await archiveTemplate(t.id);
                            const items = await listTemplatesArchived(templatesArchived);
                            setTemplates(items);
                          } finally {
                            setBusy(null);
                          }
                        })();
                      }}
                    >
                      {templatesArchived ? "Вернуть" : "В архив"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="tabsBar">
          <div className="tabsBar__inner">
            {tabs.map((t, idx) => (
              <button
                key={t.id}
                className={cn("tabBtn", t.id === activeTabId && "tabBtn--active")}
                onClick={() => switchTab(t.id)}
                {...icoA11y(`Вкладка ${idx + 1}: ${t.doc.name || "без названия"}`)}
              >
                <span className="tabBtn__txt">{t.doc.name || `Вкладка ${idx + 1}`}</span>
                {tabs.length > 1 ? (
                  <span
                    className="tabBtn__x"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      closeTab(t.id);
                    }}
                    {...icoA11y("Закрыть вкладку")}
                  >
                    ×
                  </span>
                ) : null}
              </button>
            ))}
            <button className="tabAddBtn" disabled={tabs.length >= 8} onClick={() => addTab()} {...icoA11y("Новая вкладка")}>
              +
            </button>
          </div>
        </div>

        <div className={cn("shell", isMobile && "shell--mobile")}>
        <div className={cn("panel", isMobile && mobileTab !== "tools" && "u-hidden")}>
          <div className="h2">Наклейка</div>
          <div className="field">
            <div className="label">Название</div>
            <input className="input" value={doc.name} onChange={(e) => setDocUndo((d) => ({ ...d, name: e.target.value }))} />
          </div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <div className="label">Ширина (мм)</div>
              <input
                className="input"
                inputMode="numeric"
                value={sizeWText}
                onChange={(e) => setSizeWText(e.target.value)}
                onBlur={() => {
                  const n = Number(String(sizeWText).replace(",", "."));
                  if (Number.isFinite(n)) setDocUndo((d) => ({ ...d, sizeMM: { ...d.sizeMM, w: clamp(n, 10, 5000) } }));
                  else setSizeWText(String(doc.sizeMM.w));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                }}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <div className="label">Высота (мм)</div>
              <input
                className="input"
                inputMode="numeric"
                value={sizeHText}
                onChange={(e) => setSizeHText(e.target.value)}
                onBlur={() => {
                  const n = Number(String(sizeHText).replace(",", "."));
                  if (Number.isFinite(n)) setDocUndo((d) => ({ ...d, sizeMM: { ...d.sizeMM, h: clamp(n, 10, 5000) } }));
                  else setSizeHText(String(doc.sizeMM.h));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
                }}
              />
            </div>
          </div>
          <div className="row">
            <button className="btn" onClick={() => setDocUndo((d) => ({ ...d, bw: !d.bw }))}>
              Режим: {doc.bw ? "Ч/Б" : "Цветной"}
            </button>
            <div className="small">Цвет включится в настройках элементов</div>
          </div>

          <div className="hr" />
          <div className="h2">Добавить</div>
          <div className="toolIconRow" aria-label="Элементы">
            <button type="button" className="icoBtn" {...icoA11y("Добавить текст")} onClick={addText}>
              <span aria-hidden="true">
                <IcoText />
              </span>
            </button>
            <label className="icoBtn" {...icoA11y("Добавить картинку с устройства")} style={{ cursor: "pointer" }}>
              <span aria-hidden="true">
                <IcoImage />
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onAddImage(f);
                  e.currentTarget.value = "";
                }}
              />
            </label>
            <button type="button" className="icoBtn" {...icoA11y("Добавить линию")} onClick={addLine}>
              <span aria-hidden="true">
                <IcoLine />
              </span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить прямоугольник")} onClick={addRect}>
              <span aria-hidden="true">
                <IcoRect />
              </span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить круг")} onClick={addCircle}>
              <span aria-hidden="true">
                <IcoCircle />
              </span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить штрихкод EAN‑13")} onClick={() => addBarcode("EAN13")}>
              <span aria-hidden="true">
                <IcoBarcode />
              </span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить штрихкод Code128")} onClick={() => addBarcode("CODE128")}>
              <span className="icoBtn__mono" aria-hidden="true">
                128
              </span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить QR‑код")} onClick={addQrCode}>
              <span aria-hidden="true">
                <IcoQr />
              </span>
            </button>
            <button type="button" className="icoBtn icoBtn--mark" {...icoA11y("Добавить знак маркировки EAC (ЕАС)")} onClick={() => addIcon("EAC")}>
              <span className="icoBtn__markTxt">EAC</span>
            </button>
            <button type="button" className="icoBtn icoBtn--mark" {...icoA11y("Добавить знак CE")} onClick={() => addIcon("CE")}>
              <span className="icoBtn__markTxt">CE</span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить знак класса электробезопасности I")} onClick={() => addIcon("CLASS_I")}>
              <span aria-hidden="true">
                <IcoClass n={1} />
              </span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить знак класса II")} onClick={() => addIcon("CLASS_II")}>
              <span aria-hidden="true">
                <IcoClass n={2} />
              </span>
            </button>
            <button type="button" className="icoBtn" {...icoA11y("Добавить знак класса III")} onClick={() => addIcon("CLASS_III")}>
              <span aria-hidden="true">
                <IcoClass n={3} />
              </span>
            </button>
          </div>

          <div className="hr" />
          <div className="h2">Сетка</div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <div className="label">Колонки</div>
              <input className="input" type="number" min={2} max={12} value={columns} onChange={(e) => setColumns(clamp(Number(e.target.value), 2, 12))} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <div className="label">Отступ (px)</div>
              <input className="input" type="number" min={0} max={80} value={gutter} onChange={(e) => setGutter(clamp(Number(e.target.value), 0, 80))} />
            </div>
          </div>
          <div className="small">Shift+клик — мультивыделение. Перетаскивание «липнет» к колонкам.</div>
          <div className="toolIconRow" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="icoBtn"
              {...icoA11y("Привязать к линии колонки по левому краю")}
              disabled={selectedIds.length < 1}
              onClick={() => snapSelectionToColumns("left")}
            >
              <span aria-hidden="true">
                <IcoAlignL />
              </span>
            </button>
            <button
              type="button"
              className="icoBtn"
              {...icoA11y("Привязать к линии колонки по центру")}
              disabled={selectedIds.length < 1}
              onClick={() => snapSelectionToColumns("center")}
            >
              <span aria-hidden="true">
                <IcoAlignC />
              </span>
            </button>
            <button
              type="button"
              className="icoBtn"
              {...icoA11y("Привязать к линии колонки по правому краю")}
              disabled={selectedIds.length < 1}
              onClick={() => snapSelectionToColumns("right")}
            >
              <span aria-hidden="true">
                <IcoAlignR />
              </span>
            </button>
          </div>

          <div className="hr" />
          <div className="h2">Копии</div>
          <div className="row">
            <button className="btn" disabled={selectedIds.length < 1} onClick={duplicateSelected}>
              Дублировать
            </button>
          </div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <div className="label">Копий</div>
              <input className="input" type="number" min={1} max={200} value={dupCount} onChange={(e) => setDupCount(clamp(Number(e.target.value), 1, 200))} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <div className="label">Шаг X (px)</div>
              <input className="input" type="number" value={dupStepX} onChange={(e) => setDupStepX(Number(e.target.value))} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <div className="label">Шаг Y (px)</div>
              <input className="input" type="number" value={dupStepY} onChange={(e) => setDupStepY(Number(e.target.value))} />
            </div>
          </div>
          <div className="row">
            <button className="btn" disabled={selectedIds.length < 1} onClick={() => duplicateSeries(dupStepX, 0, dupCount)}>
              В ряд →
            </button>
            <button className="btn" disabled={selectedIds.length < 1} onClick={() => duplicateSeries(0, dupStepY || 16, dupCount)}>
              В колонку ↓
            </button>
            <button className="btn" disabled={selectedIds.length < 1} onClick={() => duplicateSeries(dupStepX, dupStepY || 16, dupCount)}>
              Диагональ ↘
            </button>
          </div>

          {/* управление шаблонами перенесено в шапку */}
        </div>

        <div className={cn("stageWrap", isMobile && mobileTab !== "canvas" && "u-hidden")}>
          <div className="stageArea">
            <div className="stageCard">
              {isMobile ? (
                <div className="canvasToolbar canvasToolbar--mobile" aria-label="История">
                  <div className="canvasToolbar__row">
                    <button type="button" className="icoBtn" {...icoA11y("Назад: отменить действие (Ctrl+Z)")} onClick={undo}>
                      <span aria-hidden="true">
                        <IcoUndo />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Вперёд: повторить отменённое (Ctrl+Shift+Z)")} onClick={redo}>
                      <span aria-hidden="true">
                        <IcoRedo />
                      </span>
                    </button>
                    <button type="button" className="btn" onClick={copySelected} disabled={selectedIds.length === 0} {...icoA11y("Копировать элементы (Ctrl+C)")}>
                      Копировать
                    </button>
                    <button type="button" className="btn" onClick={pasteClipboard} disabled={!clipboardRef.current?.length} {...icoA11y("Вставить элементы (Ctrl+V)")}>
                      Вставить
                    </button>
                    <div className="zoomCtl" aria-label="Масштаб">
                      <span className="small">Масштаб</span>
                      <input
                        className="zoomCtl__range"
                        type="range"
                        min={0.25}
                        max={2}
                        step={0.05}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                      />
                      <span className="small mono">{Math.round(zoom * 100)}%</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="canvasToolbar" aria-label="Выравнивание и слои">
                  <div className="canvasToolbar__row">
                    <button type="button" className="icoBtn" {...icoA11y("Назад: отменить действие (Ctrl+Z)")} onClick={undo}>
                      <span aria-hidden="true">
                        <IcoUndo />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Вперёд: повторить (Ctrl+Shift+Z или Ctrl+Y)")} onClick={redo}>
                      <span aria-hidden="true">
                        <IcoRedo />
                      </span>
                    </button>
                    <button type="button" className="btn" onClick={copySelected} disabled={selectedIds.length === 0} {...icoA11y("Копировать элементы (Ctrl+C)")}>
                      Копировать
                    </button>
                    <button type="button" className="btn" onClick={pasteClipboard} disabled={!clipboardRef.current?.length} {...icoA11y("Вставить элементы (Ctrl+V)")}>
                      Вставить
                    </button>
                    <div className="zoomCtl" aria-label="Масштаб">
                      <span className="small">Масштаб</span>
                      <input
                        className="zoomCtl__range"
                        type="range"
                        min={0.25}
                        max={2}
                        step={0.05}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                      />
                      <span className="small mono">{Math.round(zoom * 100)}%</span>
                    </div>
                    <span className="toolbarSep" aria-hidden="true" />
                    <button type="button" className="icoBtn" {...icoA11y("Переместить выделение на передний план")} disabled={selectedIds.length < 1} onClick={() => reorderSelected("front")}>
                      <span aria-hidden="true">
                        <IcoLayerUp />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Переместить выделение на задний план")} disabled={selectedIds.length < 1} onClick={() => reorderSelected("back")}>
                      <span aria-hidden="true">
                        <IcoLayerDown />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Сдвинуть слой вперёд на один шаг")} disabled={selectedIds.length < 1} onClick={() => reorderSelected("forward")}>
                      <span aria-hidden="true">
                        <IcoLayerFwd />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Сдвинуть слой назад на один шаг")} disabled={selectedIds.length < 1} onClick={() => reorderSelected("backward")}>
                      <span aria-hidden="true">
                        <IcoLayerBack />
                      </span>
                    </button>
                    <span className="toolbarSep" aria-hidden="true" />
                    <button type="button" className="icoBtn" {...icoA11y("Равные интервалы по горизонтали между крайними в выделении (от 3 объектов)")} disabled={selectedIds.length < 3} onClick={() => distributeSelection("x")}>
                      <span aria-hidden="true">
                        <IcoDistX />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Равные интервалы по вертикали между крайними в выделении (от 3 объектов)")} disabled={selectedIds.length < 3} onClick={() => distributeSelection("y")}>
                      <span aria-hidden="true">
                        <IcoDistY />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Равномерно распределить по ширине наклейки (от 2 объектов)")} disabled={selectedIds.length < 2} onClick={() => distributeInLabel("x")}>
                      <span aria-hidden="true">
                        <IcoSpreadX />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Равномерно распределить по высоте наклейки (от 2 объектов)")} disabled={selectedIds.length < 2} onClick={() => distributeInLabel("y")}>
                      <span aria-hidden="true">
                        <IcoSpreadY />
                      </span>
                    </button>
                  </div>
                  <div className="canvasToolbar__row">
                    <span className="canvasToolbar__hint">К друг другу</span>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять выделенные по левому краю друг к другу")} disabled={selectedIds.length < 2} onClick={() => alignSelection("left")}>
                      <span aria-hidden="true">
                        <IcoAlignL />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять выделенные по центру по горизонтали")} disabled={selectedIds.length < 2} onClick={() => alignSelection("hcenter")}>
                      <span aria-hidden="true">
                        <IcoAlignC />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять выделенные по правому краю друг к другу")} disabled={selectedIds.length < 2} onClick={() => alignSelection("right")}>
                      <span aria-hidden="true">
                        <IcoAlignR />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять выделенные по верхнему краю")} disabled={selectedIds.length < 2} onClick={() => alignSelection("top")}>
                      <span aria-hidden="true">
                        <IcoAlignT />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять выделенные по вертикальному центру")} disabled={selectedIds.length < 2} onClick={() => alignSelection("vcenter")}>
                      <span aria-hidden="true">
                        <IcoAlignM />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять выделенные по нижнему краю")} disabled={selectedIds.length < 2} onClick={() => alignSelection("bottom")}>
                      <span aria-hidden="true">
                        <IcoAlignB />
                      </span>
                    </button>
                    <span className="toolbarSep" aria-hidden="true" />
                    <span className="canvasToolbar__hint">Наклейка</span>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять к левому краю наклейки")} disabled={selectedIds.length < 1} onClick={() => alignToLabel("left")}>
                      <span aria-hidden="true">
                        <IcoAlignL />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять к центру наклейки по горизонтали")} disabled={selectedIds.length < 1} onClick={() => alignToLabel("hcenter")}>
                      <span aria-hidden="true">
                        <IcoAlignC />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять к правому краю наклейки")} disabled={selectedIds.length < 1} onClick={() => alignToLabel("right")}>
                      <span aria-hidden="true">
                        <IcoAlignR />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять к верхнему краю наклейки")} disabled={selectedIds.length < 1} onClick={() => alignToLabel("top")}>
                      <span aria-hidden="true">
                        <IcoAlignT />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять к вертикальному центру наклейки")} disabled={selectedIds.length < 1} onClick={() => alignToLabel("vcenter")}>
                      <span aria-hidden="true">
                        <IcoAlignM />
                      </span>
                    </button>
                    <button type="button" className="icoBtn" {...icoA11y("Выровнять к нижнему краю наклейки")} disabled={selectedIds.length < 1} onClick={() => alignToLabel("bottom")}>
                      <span aria-hidden="true">
                        <IcoAlignB />
                      </span>
                    </button>
                  </div>
                </div>
              )}
              <div ref={containerRef} className="stageScroll">
                <Stage
                  ref={stageRef}
                  width={exporting ? stageW : Math.max(10, Math.round(stageW * stageScale))}
                  height={exporting ? stageH : Math.max(10, Math.round(stageH * stageScale))}
                  scaleX={exporting ? 1 : stageScale}
                  scaleY={exporting ? 1 : stageScale}
                  listening={!exporting}
                onMouseDown={(e) => {
                  if (exporting) return;
                  const stage = e.target.getStage();
                  if (!stage) return;
                  if (e.target !== stage) return;
                  const p = stage.getPointerPosition();
                  if (!p) return;
                  const pt = stage.getAbsoluteTransform().copy().invert().point(p);
                  setIsSelecting(true);
                  setSelectStart({ x: pt.x, y: pt.y });
                  setSelectRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
                }}
                onMouseMove={(e) => {
                  if (exporting) return;
                  if (!isSelecting || !selectStart) return;
                  const stage = e.target.getStage();
                  if (!stage) return;
                  const p = stage.getPointerPosition();
                  if (!p) return;
                  const pt = stage.getAbsoluteTransform().copy().invert().point(p);
                  setSelectRect(rectFromPoints(selectStart, { x: pt.x, y: pt.y }));
                }}
                onMouseUp={(e) => {
                  if (exporting) return;
                  if (!isSelecting) return;
                  const stage = e.target.getStage();
                  setIsSelecting(false);
                  const sr = selectRect;
                  setSelectStart(null);
                  setSelectRect(null);
                  if (!stage || !sr) {
                    setSelectedIds([]);
                    return;
                  }
                  // Click without drag clears selection.
                  if (sr.w < 4 && sr.h < 4) {
                    setSelectedIds([]);
                    return;
                  }
                  const picked = doc.nodes
                    .filter((n) => intersects(sr, getNodeBounds(n)))
                    .map((n) => n.id);
                  setSelectedIds(picked);
                }}
                onTouchStart={(e) => {
                  if (exporting) return;
                  // For now: keep touch simple (tap to select).
                  if (e.target === e.target.getStage()) setSelectedIds([]);
                }}
              >
                <Layer listening={!exporting}>
                  <Rect x={0} y={0} width={stageW} height={stageH} fill={exporting ? "#ffffff" : "rgba(255,255,255,.6)"} listening={false} />

                  {exporting ? <Rect x={labelX} y={labelY} width={labelW} height={labelH} fill="#ffffff" listening={false} /> : null}

                  {/* Column guides */}
                  {!exporting
                    ? guideInfo.guidesX.map((x, i) => (
                        <Line key={`gx${i}`} points={[x, 0, x, stageH]} stroke="rgba(0,0,0,.06)" strokeWidth={1} listening={false} />
                      ))
                    : null}
                  {!exporting
                    ? guideInfo.guidesY.map((y, i) => (
                        <Line key={`gy${i}`} points={[0, y, stageW, y]} stroke="rgba(0,0,0,.06)" strokeWidth={1} listening={false} />
                      ))
                    : null}

                  {/* Label bounds */}
                  {!exporting ? (
                    <Rect x={labelX} y={labelY} width={labelW} height={labelH} stroke="rgba(0,0,0,.25)" dash={[6, 6]} listening={false} />
                  ) : null}

                  {/* Drag-select rectangle */}
                  {selectRect ? (
                    <Rect
                      x={selectRect.x}
                      y={selectRect.y}
                      width={selectRect.w}
                      height={selectRect.h}
                      fill="rgba(31,31,31,.06)"
                      stroke="rgba(31,31,31,.35)"
                      strokeWidth={1}
                      dash={[6, 4]}
                      listening={false}
                    />
                  ) : null}

                  {doc.nodes.map((n) => (
                    <NodeView
                      key={n.id}
                      node={n}
                      isSelected={!exporting && selectedIds.includes(n.id)}
                      readOnly={exporting}
                      bw={doc.bw}
                      stageW={stageW}
                      stageH={stageH}
                      onSelect={(additive) =>
                        setSelectedIds((prev) => {
                          if (!additive) return [n.id];
                          return prev.includes(n.id) ? prev.filter((x) => x !== n.id) : [...prev, n.id];
                        })
                      }
                      onChange={(patch) => updateNode(n.id, patch)}
                      columns={columns}
                      gutter={gutter}
                      setRef={(ref) => {
                        if (ref) nodeRefs.current.set(n.id, ref);
                        else nodeRefs.current.delete(n.id);
                      }}
                    />
                  ))}

                  <Transformer
                    ref={transformerRef}
                    visible={!exporting}
                    listening={!exporting}
                    rotateEnabled
                    flipEnabled
                    keepRatio={transformerKeepRatio}
                    padding={10}
                    rotateAnchorOffset={36}
                    enabledAnchors={["top-left", "top-right", "bottom-left", "bottom-right", "middle-left", "middle-right", "top-center", "bottom-center"]}
                    boundBoxFunc={(oldBox, newBox) => {
                      // Lines can have near-zero height/width; don't block transform.
                      if (selected?.type === "line") {
                        if (newBox.width < 5) return oldBox;
                        return newBox;
                      }
                      if (newBox.width < 5 || newBox.height < 5) return oldBox;
                      return newBox;
                    }}
                  />
                </Layer>
                </Stage>
              </div>
              <div className="small" style={{ marginTop: 10 }}>
                Подсказка: клик — выбрать элемент, Shift+клик — мультивыделение, Delete — удалить, перетаскивание — со “снапом”.
              </div>
            </div>
          </div>
        </div>

        <div className={cn("panel", "right", isMobile && mobileTab !== "props" && "u-hidden")}>
          <div className="h2">Свойства</div>
          {selectedIds.length === 0 ? (
            <div className="small">Выберите элемент на холсте. Shift+клик — мультивыделение.</div>
          ) : selectedIds.length > 1 ? (
            <div className="small">
              Выбрано элементов: <b>{selectedIds.length}</b>. Выравнивание — панель над холстом (на десктопе).
            </div>
          ) : (
            <PropsPanel doc={doc} node={selected!} onChange={(p) => updateNode(selected!.id, p)} onDelete={removeSelected} />
          )}
        </div>
        {isMobile ? (
          <nav className="mobileTabBar" role="tablist" aria-label="Разделы редактора">
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "tools"}
              className={cn("mobileTab", mobileTab === "tools" && "mobileTab--active")}
              onClick={() => setMobileTab("tools")}
            >
              Макет
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "canvas"}
              className={cn("mobileTab", mobileTab === "canvas" && "mobileTab--active")}
              onClick={() => setMobileTab("canvas")}
            >
              Холст
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "props"}
              className={cn("mobileTab", mobileTab === "props" && "mobileTab--active")}
              onClick={() => setMobileTab("props")}
            >
              Свойства
            </button>
          </nav>
        ) : null}
      </div>
      </div>
      <CookieBanner />
      <KeyHandler
        onDelete={removeSelected}
        onDuplicate={duplicateSelected}
        onBringFront={() => reorderSelected("front")}
        onSendBack={() => reorderSelected("back")}
        onUndo={undo}
        onRedo={redo}
        onCopy={copySelected}
        onPaste={pasteClipboard}
        isTypingTarget={isTypingTarget}
      />
    </>
  );
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error?: Error; info?: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = {};
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: any) {
    // eslint-disable-next-line no-console
    console.error("labler crashed", error, info);
    this.setState({ info: String(info?.componentStack ?? "") });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: 18, maxWidth: 980, margin: "0 auto" }}>
        <div style={{ fontWeight: 520, marginBottom: 8 }}>Ошибка в приложении</div>
        <div className="small" style={{ marginBottom: 12 }}>
          Скопируйте текст ниже и пришлите в чат — я быстро починю.
        </div>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            border: "1px solid rgba(0,0,0,.12)",
            borderRadius: 14,
            background: "rgba(255,255,255,.6)",
            padding: 12,
            overflow: "auto",
          }}
        >
{(() => {
            const err = this.state.error;
            const msg =
              err instanceof Error
                ? err.message || "(без сообщения)"
                : err != null
                  ? String(err)
                  : "unknown";
            const stack = err instanceof Error && err.stack ? err.stack : "";
            const base = [msg, stack].filter(Boolean).join("\n\n");
            return this.state.info ? `${base}\n\n${this.state.info}` : base;
          })()}
        </pre>
      </div>
    );
  }
}

function KonvaQrNode({
  node,
  common,
  readOnly,
  onChange,
  bw,
}: {
  node: NodeQrcode;
  common: Record<string, unknown>;
  readOnly: boolean;
  onChange: (patch: Partial<NodeAny>) => void;
  bw: boolean;
}) {
  const px = Math.max(160, Math.round(Math.max(node.w, node.h) * 2.5));
  const dark = bw ? "#111111" : node.foreground === "transparent" ? "#111111" : node.foreground;
  const light = node.background === "transparent" ? "#ffffff" : node.background;
  const [dataUrl, setDataUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    const t = node.value.trim() || " ";
    QRCode.toDataURL(t, {
      width: px,
      margin: 1,
      color: { dark, light },
      errorCorrectionLevel: "M",
    })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDataUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [node.value, dark, light, px]);
  const img = useKonvaImage(dataUrl);
  return (
    <KImage
      {...(common as any)}
      image={img ?? undefined}
      width={node.w}
      height={node.h}
      onTransformEnd={(e) => {
        if (readOnly) return;
        const n = e.target;
        const sx = n.scaleX();
        const sy = n.scaleY();
        n.scaleX(1);
        n.scaleY(1);
        const tr = (n.getStage()?.findOne("Transformer") as any) ?? null;
        const anchor = (tr?.getActiveAnchor?.() as string | undefined) ?? "";
        const isCorner = anchor.includes("top-") || anchor.includes("bottom-");
        const s = Math.max(Math.abs(sx), Math.abs(sy));
        if (isCorner) onChange({ w: Math.max(10, node.w * s), h: Math.max(10, node.h * s) } as any);
        else onChange({ w: Math.max(10, node.w * sx), h: Math.max(10, node.h * sy) } as any);
      }}
    />
  );
}

function KeyHandler({
  onDelete,
  onDuplicate,
  onBringFront,
  onSendBack,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  isTypingTarget,
}: {
  onDelete: () => void;
  onDuplicate: () => void;
  onBringFront: () => void;
  onSendBack: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onCopy: () => void;
  onPaste: () => void;
  isTypingTarget: (t: any) => boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        if (e.shiftKey) onRedo();
        else onUndo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || e.key === "Y")) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        onRedo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C")) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        onCopy();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V")) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        onPaste();
        return;
      }
      if (e.key === "Delete" || (e.key === "Backspace" && !isTypingTarget(e.target))) {
        onDelete();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        onDuplicate();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "]" || e.key === "}")) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        onBringFront();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "[" || e.key === "{")) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        onSendBack();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDelete, onDuplicate, onBringFront, onSendBack, onUndo, onRedo, onCopy, onPaste, isTypingTarget]);
  return null;
}

function NodeView({
  node,
  isSelected,
  readOnly = false,
  bw,
  stageW,
  stageH,
  onSelect,
  onChange,
  columns,
  gutter,
  setRef,
}: {
  node: NodeAny;
  isSelected: boolean;
  readOnly?: boolean;
  bw: boolean;
  stageW: number;
  stageH: number;
  onSelect: (additive: boolean) => void;
  onChange: (patch: Partial<NodeAny>) => void;
  columns: number;
  gutter: number;
  setRef: (ref: any | null) => void;
}) {
  const shapeRef = useRef<any>(null);
  useEffect(() => {
    setRef(shapeRef.current);
    return () => setRef(null);
  }, [setRef]);

  const common = {
    x: node.x,
    y: node.y,
    rotation: node.rotation,
    opacity: node.opacity,
    draggable: !readOnly,
    listening: !readOnly,
    onClick: (e: any) => {
      if (readOnly) return;
      onSelect(Boolean(e?.evt?.shiftKey));
    },
    onTap: () => {
      if (readOnly) return;
      onSelect(false);
    },
    onDragMove: (e: any) => {
      if (readOnly) return;
      const pos = e.target.position();
      const s = snapToGuides(pos.x, pos.y, columns, gutter, stageW, stageH);
      e.target.position({ x: s.x, y: s.y });
    },
    onDragEnd: (e: any) => {
      if (readOnly) return;
      const pos = e.target.position();
      onChange({ x: pos.x, y: pos.y });
    },
    ref: shapeRef,
  } as const;

  const colorFix = (c: string) => {
    if (c === "transparent") return "rgba(0,0,0,0)";
    return bw ? "#111111" : c;
  };

  function transformMode(n: any): "uniform" | "x" | "y" | "free" {
    const tr = (n.getStage()?.findOne("Transformer") as any) ?? null;
    const a = (tr?.getActiveAnchor?.() as string | undefined) ?? "";
    if (a === "top-left" || a === "top-right" || a === "bottom-left" || a === "bottom-right") return "uniform";
    if (a === "middle-left" || a === "middle-right") return "x";
    if (a === "top-center" || a === "bottom-center") return "y";
    return "free";
  }

  let nodeEl: React.ReactNode = null;
  if (node.type === "text") {
    nodeEl = (
      <Text
        {...common}
        text={node.text}
        fontFamily={node.fontFamily}
        fontSize={node.fontSize}
        fontStyle={node.fontStyle}
        fontVariant="normal"
        fontWeight={node.fontWeight}
        fill={colorFix(node.fill)}
        width={node.width}
        align={node.align}
        listening={!readOnly}
        onTransformEnd={(e) => {
          if (readOnly) return;
          const n = e.target;
          const scaleX = n.scaleX();
          n.scaleX(1);
          onChange({ width: Math.max(10, node.width * scaleX) } as any);
        }}
      />
    );
  } else if (node.type === "image") {
    const img = useKonvaImage(node.srcDataUrl);
    useEffect(() => {
      const s = shapeRef.current;
      if (!s) return;
      if (bw) {
        s.cache();
        s.filters([Konva.Filters.Grayscale]);
        s.getLayer()?.batchDraw();
      } else {
        s.filters([]);
        s.clearCache();
        s.getLayer()?.batchDraw();
      }
    }, [bw, node.srcDataUrl]);
    nodeEl = (
      <KImage
        {...common}
        image={img ?? undefined}
        width={node.w}
        height={node.h}
        onTransformEnd={(e) => {
          if (readOnly) return;
          const n = e.target;
          const scaleX = n.scaleX();
          const scaleY = n.scaleY();
          n.scaleX(1);
          n.scaleY(1);
          const mode = transformMode(n);
          const s = Math.max(Math.abs(scaleX), Math.abs(scaleY));
          if (node.lockAspect || mode === "uniform") {
            onChange({ w: Math.max(5, node.w * s), h: Math.max(5, node.h * s) } as any);
          } else if (mode === "x") {
            onChange({ w: Math.max(5, node.w * scaleX) } as any);
          } else if (mode === "y") {
            onChange({ h: Math.max(5, node.h * scaleY) } as any);
          } else {
            onChange({ w: Math.max(5, node.w * scaleX), h: Math.max(5, node.h * scaleY) } as any);
          }
        }}
      />
    );
  } else if (node.type === "line") {
    nodeEl = (
      <Line
        {...common}
        points={node.points}
        stroke={colorFix(node.stroke)}
        strokeWidth={node.strokeWidth}
        lineCap="round"
        lineJoin="round"
        hitStrokeWidth={24}
        perfectDrawEnabled={false}
        onTransformEnd={(e) => {
          if (readOnly) return;
          const n = e.target;
          const sx = n.scaleX();
          const sy = n.scaleY();
          n.scaleX(1);
          n.scaleY(1);
          const p = node.points;
          const pts = p.map((v, i) => (i % 2 === 0 ? v * sx : v * sy));
          onChange({ points: pts } as any);
        }}
      />
    );
  } else if (node.type === "rect") {
    nodeEl = (
      <Rect
        {...common}
        width={node.w}
        height={node.h}
        stroke={node.stroke === "transparent" ? "rgba(0,0,0,0)" : bw ? "#111111" : node.stroke}
        strokeWidth={node.strokeWidth}
        fill={colorFix(node.fill)}
        cornerRadius={node.cornerRadius}
        perfectDrawEnabled={false}
        onTransformEnd={(e) => {
          if (readOnly) return;
          const n = e.target;
          const sx = n.scaleX();
          const sy = n.scaleY();
          n.scaleX(1);
          n.scaleY(1);
          onChange({ w: Math.max(5, node.w * sx), h: Math.max(5, node.h * sy) } as any);
        }}
      />
    );
  } else if (node.type === "circle") {
    nodeEl = (
      <Circle
        {...common}
        radius={node.r}
        stroke={colorFix(node.stroke)}
        strokeWidth={node.strokeWidth}
        fill={colorFix(node.fill)}
        onTransformEnd={(e) => {
          if (readOnly) return;
          const n = e.target;
          const sx = n.scaleX();
          const sy = n.scaleY();
          n.scaleX(1);
          n.scaleY(1);
          const u = Math.max(Math.abs(sx), Math.abs(sy));
          onChange({ r: Math.max(3, node.r * u) } as any);
        }}
      />
    );
  } else if (node.type === "barcode") {
    const dataUrl = useMemo(
      () =>
        makeBarcodeDataUrl({
          format: node.format,
          value: node.value,
          lineColor: bw ? "#111111" : node.lineColor,
          background: node.background,
          h: node.h,
        }),
      [node.format, node.value, node.lineColor, node.background, node.h, bw],
    );
    const img = useKonvaImage(dataUrl);
    nodeEl = (
      <KImage
        {...common}
        image={img ?? undefined}
        width={node.w}
        height={node.h}
        onTransformEnd={(e) => {
          if (readOnly) return;
          const n = e.target;
          const sx = n.scaleX();
          const sy = n.scaleY();
          n.scaleX(1);
          n.scaleY(1);
          const mode = transformMode(n);
          const s = Math.max(Math.abs(sx), Math.abs(sy));
          if (mode === "uniform") onChange({ w: Math.max(10, node.w * s), h: Math.max(10, node.h * s) } as any);
          else if (mode === "x") onChange({ w: Math.max(10, node.w * sx) } as any);
          else if (mode === "y") onChange({ h: Math.max(10, node.h * sy) } as any);
          else onChange({ w: Math.max(10, node.w * sx), h: Math.max(10, node.h * sy) } as any);
        }}
      />
    );
  } else if (node.type === "qrcode") {
    nodeEl = <KonvaQrNode node={node} common={common as any} readOnly={readOnly} onChange={onChange} bw={bw} />;
  } else if (node.type === "icon") {
    const fill = bw ? "#111111" : node.fill;
    const svg = useMemo(() => iconToSvg(node.icon, node.w, node.h, fill), [node.icon, node.w, node.h, fill]);
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    const img = useKonvaImage(dataUrl);
    nodeEl = (
      <KImage
        {...common}
        image={img ?? undefined}
        width={node.w}
        height={node.h}
        onTransformEnd={(e) => {
          if (readOnly) return;
          const n = e.target;
          const sx = n.scaleX();
          const sy = n.scaleY();
          n.scaleX(1);
          n.scaleY(1);
          const mode = transformMode(n);
          const s = Math.max(Math.abs(sx), Math.abs(sy));
          if (mode === "uniform") onChange({ w: Math.max(10, node.w * s), h: Math.max(10, node.h * s) } as any);
          else if (mode === "x") onChange({ w: Math.max(10, node.w * sx) } as any);
          else if (mode === "y") onChange({ h: Math.max(10, node.h * sy) } as any);
          else onChange({ w: Math.max(10, node.w * sx), h: Math.max(10, node.h * sy) } as any);
        }}
      />
    );
  }

  return (
    <>
      {nodeEl}
    </>
  );
}

function PropsPanel({
  doc,
  node,
  onChange,
  onDelete,
}: {
  doc: DocV1;
  node: NodeAny;
  onChange: (patch: Partial<NodeAny>) => void;
  onDelete: () => void;
}) {
  const commonPos = (
    <>
      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <div className="label">X</div>
          <input className="input" type="number" value={Math.round(node.x)} onChange={(e) => onChange({ x: Number(e.target.value) } as any)} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <div className="label">Y</div>
          <input className="input" type="number" value={Math.round(node.y)} onChange={(e) => onChange({ y: Number(e.target.value) } as any)} />
        </div>
      </div>
      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <div className="label">Поворот</div>
          <input className="input" type="number" value={Math.round(node.rotation)} onChange={(e) => onChange({ rotation: Number(e.target.value) } as any)} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <div className="label">Прозрачность</div>
          <input className="input" type="number" min={0} max={1} step={0.05} value={node.opacity} onChange={(e) => onChange({ opacity: Number(e.target.value) } as any)} />
        </div>
      </div>
      <button className="btn" onClick={onDelete}>
        Удалить элемент
      </button>
    </>
  );

  const colorPicker = (value: string, on: (v: string) => void) => (
    <input
      className="input"
      type="color"
      value={value.startsWith("#") ? value : "#111111"}
      disabled={doc.bw}
      onChange={(e) => on(e.target.value)}
      title={doc.bw ? "В Ч/Б режиме цвет фиксирован" : "Выберите цвет"}
    />
  );

  if (node.type === "text") {
    return (
      <div>
        <div className="field">
          <div className="label">Текст</div>
          <textarea
            className="input"
            style={{ minHeight: 120, resize: "vertical" }}
            value={node.text}
            onChange={(e) => onChange({ text: e.target.value } as any)}
          />
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Шрифт</div>
            <select className="select" value={node.fontFamily} onChange={(e) => onChange({ fontFamily: e.target.value } as any)}>
              {FONT_FAMILIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Размер</div>
            <input className="input" type="number" min={6} max={220} value={node.fontSize} onChange={(e) => onChange({ fontSize: Number(e.target.value) } as any)} />
          </div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Толщина</div>
            <select className="select" value={node.fontWeight} onChange={(e) => onChange({ fontWeight: Number(e.target.value) } as any)}>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Выравнивание</div>
            <select className="select" value={node.align} onChange={(e) => onChange({ align: e.target.value } as any)}>
              <option value="left">Слева</option>
              <option value="center">По центру</option>
              <option value="right">Справа</option>
            </select>
          </div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Ширина блока</div>
            <input className="input" type="number" min={10} max={1200} value={Math.round(node.width)} onChange={(e) => onChange({ width: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Цвет</div>
            {colorPicker(node.fill, (v) => onChange({ fill: v } as any))}
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  if (node.type === "line") {
    return (
      <div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Толщина (px)</div>
            <input className="input" type="number" min={1} max={30} value={node.strokeWidth} onChange={(e) => onChange({ strokeWidth: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Цвет</div>
            {colorPicker(node.stroke, (v) => onChange({ stroke: v } as any))}
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  if (node.type === "rect") {
    return (
      <div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Ширина</div>
            <input className="input" type="number" min={5} value={Math.round(node.w)} onChange={(e) => onChange({ w: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Высота</div>
            <input className="input" type="number" min={5} value={Math.round(node.h)} onChange={(e) => onChange({ h: Number(e.target.value) } as any)} />
          </div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Обводка</div>
            {colorPicker(node.stroke, (v) => onChange({ stroke: v } as any))}
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Толщина</div>
            <input className="input" type="number" min={0} max={30} value={node.strokeWidth} onChange={(e) => onChange({ strokeWidth: Number(e.target.value) } as any)} />
          </div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Заливка</div>
            {colorPicker(node.fill === "transparent" ? "#ffffff" : node.fill, (v) => onChange({ fill: v } as any))}
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Скругление</div>
            <input className="input" type="number" min={0} max={60} value={node.cornerRadius} onChange={(e) => onChange({ cornerRadius: Number(e.target.value) } as any)} />
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  if (node.type === "circle") {
    return (
      <div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Радиус</div>
            <input className="input" type="number" min={3} value={Math.round(node.r)} onChange={(e) => onChange({ r: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Обводка</div>
            {colorPicker(node.stroke, (v) => onChange({ stroke: v } as any))}
          </div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Толщина</div>
            <input className="input" type="number" min={0} max={30} value={node.strokeWidth} onChange={(e) => onChange({ strokeWidth: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Заливка</div>
            {colorPicker(node.fill === "transparent" ? "#ffffff" : node.fill, (v) => onChange({ fill: v } as any))}
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  if (node.type === "barcode") {
    return (
      <div>
        <div className="field">
          <div className="label">Формат</div>
          <select
            className="select"
            value={node.format}
            onChange={(e) => {
              const format = e.target.value as NodeBarcode["format"];
              if (format === "EAN13") {
                const digits = ean13DigitsOnly(node.value).slice(0, 13);
                onChange({
                  format,
                  value: digits.length > 0 ? digits : "5901234123457",
                } as any);
              } else {
                onChange({ format } as any);
              }
            }}
          >
            <option value="EAN13">EAN‑13</option>
            <option value="CODE128">Code128</option>
          </select>
        </div>
        <div className="field">
          <div className="label">Значение</div>
          <input
            className="input"
            value={node.value}
            onChange={(e) => {
              if (node.format === "EAN13") {
                onChange({ value: ean13DigitsOnly(e.target.value).slice(0, 13) } as any);
              } else {
                onChange({ value: e.target.value } as any);
              }
            }}
          />
          <div className="small">Под полосами показывается закодированный текст. EAN‑13 — только цифры.</div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Ширина</div>
            <input className="input" type="number" min={10} value={Math.round(node.w)} onChange={(e) => onChange({ w: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Высота</div>
            <input className="input" type="number" min={10} value={Math.round(node.h)} onChange={(e) => onChange({ h: Number(e.target.value) } as any)} />
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  if (node.type === "qrcode") {
    return (
      <div>
        <div className="field">
          <div className="label">Содержимое</div>
          <input className="input" value={node.value} onChange={(e) => onChange({ value: e.target.value } as any)} />
          <div className="small">URL или текст (например https://labler.ru). Картинка пересчитывается автоматически.</div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Ширина</div>
            <input className="input" type="number" min={20} value={Math.round(node.w)} onChange={(e) => onChange({ w: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Высота</div>
            <input className="input" type="number" min={20} value={Math.round(node.h)} onChange={(e) => onChange({ h: Number(e.target.value) } as any)} />
          </div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Модули (цвет)</div>
            {colorPicker(node.foreground === "transparent" ? "#111111" : node.foreground, (v) =>
              onChange({ foreground: v } as any),
            )}
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Фон</div>
            {colorPicker(node.background === "transparent" ? "#ffffff" : node.background, (v) =>
              onChange({ background: v } as any),
            )}
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  if (node.type === "icon") {
    return (
      <div>
        <div className="field">
          <div className="label">Иконка</div>
          <select className="select" value={node.icon} onChange={(e) => onChange({ icon: e.target.value } as any)}>
            <option value="EAC">EAC</option>
            <option value="CE">CE</option>
            <option value="CLASS_I">Класс I</option>
            <option value="CLASS_II">Класс II</option>
            <option value="CLASS_III">Класс III</option>
          </select>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Ширина</div>
            <input className="input" type="number" min={10} value={Math.round(node.w)} onChange={(e) => onChange({ w: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Высота</div>
            <input className="input" type="number" min={10} value={Math.round(node.h)} onChange={(e) => onChange({ h: Number(e.target.value) } as any)} />
          </div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Цвет</div>
            {colorPicker(node.fill, (v) => onChange({ fill: v } as any))}
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  if (node.type === "image") {
    return (
      <div>
        <div className="small">Картинка (PNG/JPG/SVG). Можно масштабировать на холсте.</div>
        <div className="row">
          <button className="btn" onClick={() => onChange({ lockAspect: !node.lockAspect } as any)}>
            Пропорции: {node.lockAspect ? "сохранять" : "свободно"}
          </button>
          <button
            className="btn"
            disabled={!(node.naturalW && node.naturalH)}
            onClick={() => {
              if (node.naturalW && node.naturalH) onChange({ w: node.naturalW, h: node.naturalH } as any);
            }}
          >
            Исходный размер
          </button>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Ширина</div>
            <input className="input" type="number" min={5} value={Math.round(node.w)} onChange={(e) => onChange({ w: Number(e.target.value) } as any)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <div className="label">Высота</div>
            <input className="input" type="number" min={5} value={Math.round(node.h)} onChange={(e) => onChange({ h: Number(e.target.value) } as any)} />
          </div>
        </div>
        {commonPos}
      </div>
    );
  }

  return <div className="small">Неизвестный тип элемента.</div>;
}

